import React, { useEffect, useRef, useState } from "react";
import { ExternalLink, Maximize2, Loader2 } from "lucide-react";

/**
 * Transformation Strategy tab — renders the senior-director S/4HANA Discovery
 * Strategy Assessment framework (Greenfield / Brownfield / Bluefield decision
 * model) in an iframe so its bespoke styles don't leak into the rest of the
 * app.
 *
 * Rendering rules to keep this stable:
 *  • Iframe is always in the DOM with a stable starting height (no display
 *    toggle — toggling display:none on an iframe reloads it in some browsers).
 *  • The spinner is overlaid on top during the initial load, not swapped in
 *    place of the iframe (prevents the "flash + blank" you see when the
 *    placeholder collapses and the iframe expands from 0 to several thousand
 *    pixels).
 *  • Height is measured once on iframe load + tracked with a ResizeObserver
 *    (fallback to a low-frequency 1500ms poll) so we never re-render in tight
 *    loops as the autosave fires.
 */
export default function Strategy() {
  const ref = useRef(null);
  // Start with a sensible, immediately-stable height — viewport-tall — so
  // the layout doesn't jump when the iframe content arrives.
  const [height, setHeight] = useState(() =>
    typeof window !== "undefined" ? Math.max(window.innerHeight - 240, 700) : 900,
  );
  const [loaded, setLoaded] = useState(false);

  // Measure + track the iframe body height. Runs ONCE on mount; the
  // ResizeObserver / interval handles updates without triggering React
  // re-renders unless the height actually changed.
  useEffect(() => {
    let ro;
    let pollId;
    let cancelled = false;

    const measure = () => {
      if (cancelled) return;
      const win = ref.current?.contentWindow;
      const body = win?.document?.body;
      const docEl = win?.document?.documentElement;
      if (!body || !docEl) return;
      const h = Math.max(body.scrollHeight, body.offsetHeight, docEl.scrollHeight, docEl.offsetHeight);
      if (!h) return;
      setHeight(prev => (Math.abs(h - prev) > 16 ? h : prev));
    };

    const start = () => {
      // Initial measurements after layout settles (fonts, images, animations).
      requestAnimationFrame(measure);
      setTimeout(measure, 400);
      setTimeout(measure, 1200);

      const body = ref.current?.contentWindow?.document?.body;
      const RO = ref.current?.contentWindow?.ResizeObserver;
      if (body && RO) {
        ro = new RO(() => measure());
        ro.observe(body);
      } else {
        // Browser fallback (rare): low-frequency poll, only while mounted.
        pollId = setInterval(measure, 1500);
      }
    };

    // If the iframe is already loaded by the time the effect runs, start now.
    if (ref.current?.contentWindow?.document?.readyState === "complete") start();

    const handleLoad = () => start();
    const node = ref.current;
    node?.addEventListener("load", handleLoad);

    return () => {
      cancelled = true;
      node?.removeEventListener("load", handleLoad);
      if (ro) ro.disconnect();
      if (pollId) clearInterval(pollId);
    };
  }, []);

  return (
    <>
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 section-eyebrow">Senior Director Assessment</div>
          <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-zinc-900 mt-1">
            Transformation Strategy
          </h2>
          <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
            A structured decision framework for Greenfield / Brownfield / Bluefield SAP&nbsp;S/4HANA transformation — by AJ.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a
            href="/strategy.html"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-secondary btn-sm"
            data-testid="btn-strategy-fullscreen"
            title="Open in a new tab (full screen)"
          >
            <Maximize2 size={12}/> Fullscreen
          </a>
        </div>
      </div>

      <div className="card p-0 overflow-hidden relative" style={{ minHeight: 700 }}>
        <iframe
          ref={ref}
          src="/strategy.html"
          title="Transformation Strategy — S/4HANA Discovery Framework"
          onLoad={() => setLoaded(true)}
          style={{ width: "100%", height, border: 0, display: "block" }}
          data-testid="iframe-strategy"
          // The framework is same-origin (under /strategy.html), so no sandbox
          // — it needs its own scripts (IntersectionObserver reveals, scroll
          // animations) to run for the polished effect.
        />
        {!loaded && (
          <div className="absolute inset-0 grid place-items-center bg-white/95 backdrop-blur-sm pointer-events-none">
            <div className="flex items-center gap-2 font-mono text-xs text-zinc-500">
              <Loader2 size={14} className="animate-spin"/> Loading strategy framework…
            </div>
          </div>
        )}
      </div>

      <div className="mt-3 font-mono text-[10px] text-zinc-500 flex items-center gap-1.5">
        <ExternalLink size={11}/> Authored by Ayo Jaiyesimi (AJ) · For executive use · Embedded directly from the source framework
      </div>
    </>
  );
}
